import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, ShieldCheck, X, CheckCircle2, AlertTriangle, Download, Landmark } from "lucide-react";
import { Celebration } from "./journey/Celebration";
import { JourneyTimeline, ManualFeeDialog, useJourney } from "./journey/Journey";
import { RoleBadge, RoleSelect } from "./journey/RoleBadge";
import { StuckDirectorsStrip } from "./journey/StuckDirectorsStrip";
import { FeeWaiverControls } from "./journey/FeeWaiver";

export function AdminDirectorSuite({ api }) {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending_authorization");
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [celebrate, setCelebrate] = useState(null);
  const [wallLoading, setWallLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/directors?status=${tab}`);
      setItems(r.data);
    } catch { toast.error("Could not load director applications."); }
    finally { setLoading(false); }
  }, [api, tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener("director-fee-acknowledged", load); return () => window.removeEventListener("director-fee-acknowledged", load); }, [load]);

  const downloadFoundersWall = async () => {
    setWallLoading(true);
    try {
      const r = await api.get("/admin/founders-wall.pdf", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const a = document.createElement("a");
      a.href = url; a.download = `vinci-founders-wall-${stamp}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Founders Wall poster downloaded.");
    } catch {
      toast.error("Could not generate the Founders Wall poster.");
    } finally { setWallLoading(false); }
  };

  return (
    <div data-testid="admin-director-suite">
      {/* Super Master banner */}
      <div className="rounded-sm border-2 border-[#C9A961]/50 bg-gradient-to-br from-[#141008] via-[#0f0d0a] to-[#141008] p-6 mb-8 flex items-start gap-5">
        <span className="grid place-items-center w-12 h-12 rounded-full bg-[#C9A961] text-black shrink-0">
          <Crown size={22} />
        </span>
        <div className="flex-1">
          <div className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">
            · Super Master Command · Admin Only ·
          </div>
          <h3 className="font-serif text-2xl text-[#F5F5F0] mt-1">Executive Board Authorization</h3>
          <p className="font-body text-sm text-[#C9C9C2] mt-2 leading-relaxed">
            Every Director account is <strong>locked</strong> until you personally
            authorize it. Waiving the ₹2,50,000 fee is an override reserved for
            select strategic partners. Revoking access sends a formal Board note
            and moves them off the Presidential Portal instantly.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[
          ["pending_authorization", "Awaiting Authorization"],
          ["pending_payment", "Awaiting Fee"],
          ["active", "Active Directors"],
          ["revoked", "Revoked"],
        ].map(([k, l]) => (
          <button
            key={k}
            data-testid={`director-tab-${k}`}
            onClick={() => setTab(k)}
            className={`rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] transition-colors ${
              tab === k ? "bg-[#C9A961] text-black" : "border border-white/20 text-[#C9C9C2] hover:border-[#C9A961]"
            }`}
          >
            {l}
          </button>
        ))}
        <button
          type="button"
          data-testid="download-founders-wall-btn"
          onClick={downloadFoundersWall}
          disabled={wallLoading}
          className="ml-auto inline-flex items-center gap-2 rounded-full px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] border border-[#C9A961]/60 text-[#C9A961] hover:bg-[#C9A961]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Print the Founders Wall poster (PDF)"
        >
          <Download size={13} />
          {wallLoading ? "Preparing…" : "Founders Wall · PDF"}
        </button>
      </div>

      <StuckDirectorsStrip api={api} />

      {loading ? (
        <p className="font-body text-[#8A8A82]">Loading…</p>
      ) : items.length === 0 ? (
        <p className="font-body text-[#8A8A82]" data-testid="director-empty">
          {tab === "pending_authorization" ? "No directors awaiting Board authorization."
            : tab === "pending_payment" ? "No directors mid-payment."
            : tab === "active" ? "No active directors yet."
            : "No revoked director accounts."}
        </p>
      ) : (
        <div className="space-y-3">
          {items.map((d) => (
            <DirectorRow key={d.id} d={d} onOpen={() => setTarget(d)} tab={tab} api={api} />
          ))}
        </div>
      )}

      {target && (
        <AuthorizeModal
          director={target}
          onClose={() => setTarget(null)}
          onDone={(celebrate) => { setTarget(null); load(); if (celebrate) setCelebrate(celebrate); }}
          api={api}
        />
      )}
      {celebrate && <Celebration stage={{ n: 3, title: "Authorization", line: "Authorized. Your seat at the Vinci table is confirmed." }} name={celebrate.name} shareholdingPct={celebrate.pct} onDone={() => setCelebrate(null)} />}
    </div>
  );
}

function DirectorRow({ d, onOpen, tab, api }) {
  const journey = useJourney(api, d.id);
  const [feeOpen, setFeeOpen] = useState(false);
  const [busyStage, setBusyStage] = useState(null);
  const mark = async (n, iso) => { setBusyStage(n); try { const r = await api.put(`/admin/directors/${d.id}/journey/${n}`, { done_at: iso }); journey.setData(r.data); toast.success(`Stage ${n} marked — the celebration will play on their next login`); } catch (e) { toast.error(e.response?.data?.detail || "Could not mark stage"); } finally { setBusyStage(null); } };
  const clear = async (n) => { try { const r = await api.delete(`/admin/directors/${d.id}/journey/${n}`); journey.setData(r.data); toast.success(`Stage ${n} manual tick cleared`); } catch (e) { toast.error(e.response?.data?.detail || "Could not clear"); } };
  const stagePill = (s) => {
    const m = {
      pending_payment: { c: "#B87333", l: "Awaiting Fee" },
      pending_authorization: { c: "#C9A961", l: "Awaiting Authorization" },
      active: { c: "#2FB999", l: "Active" },
      revoked: { c: "#C0392B", l: "Revoked" },
    }[s] || { c: "#8A8A82", l: s };
    return <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] border rounded-full px-3 py-1" style={{ color: m.c, borderColor: `${m.c}66` }}>{m.l}</span>;
  };
  return (
    <div className="border border-[#C9A961]/20 rounded-sm p-6 hover:border-[#C9A961]/50 transition-colors bg-[#0f0d0a]" data-testid={`director-row-${d.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <Crown size={16} className="text-[#C9A961]" />
            <span className="font-serif text-xl md:text-2xl text-[#F5F5F0]">{d.business_name || d.name || d.email}</span>
            {journey.data?.role && <RoleBadge role={journey.data.role} size="sm" />}
            {stagePill(d.director_status)}
            {d.fee_waived && (
              <span className="font-grotesk text-[10px] uppercase tracking-[0.18em] border border-[#C9A961]/50 text-[#C9A961] rounded-full px-2.5 py-1">
                Fee waived
              </span>
            )}
          </div>
          <div className="font-body text-sm text-[#8A8A82] mt-1">
            {d.name}{d.email ? ` · ${d.email}` : ""}{d.phone ? ` · ${d.phone}` : ""}
            {d.din ? ` · DIN ${d.din}` : ""}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {tab === "active" ? (
            <button
              data-testid={`director-revoke-${d.id}`}
              onClick={onOpen}
              className="rounded-full border border-[#C0392B]/50 text-[#C0392B] px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:bg-[#C0392B] hover:text-white transition-colors"
            >
              Revoke access…
            </button>
          ) : tab === "revoked" ? (
            <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#8A8A82]">
              Revoked · {d.revoked_at ? new Date(d.revoked_at).toLocaleDateString() : "—"}
            </span>
          ) : (
            <button
              data-testid={`director-authorize-${d.id}`}
              onClick={onOpen}
              className="rounded-full bg-[#C9A961] text-black px-5 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:bg-[#8B7333] hover:text-[#F5F5F0] transition-colors"
            >
              Authorize Executive…
            </button>
          )}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3 mt-5 pt-5 border-t border-[#C9A961]/10">
        <Field l="Fee Amount" v={`₹${(d.fee_amount || 0).toLocaleString()}`} />
        <Field l="Fee Paid" v={d.fee_paid ? "Yes" : d.fee_waived ? "Waived" : "No"}
                accent={d.fee_paid ? "#2FB999" : d.fee_waived ? "#C9A961" : "#B87333"} />
        <Field l="Shareholding" v={`${(d.shareholding_pct || 0).toFixed(2)}%`} accent="#C9A961" />
        {d.pan && <Field l="PAN" v={d.pan} />}
        {d.din && <Field l="DIN" v={d.din} />}
        {d.razorpay_payment_id && <Field l="Razorpay Payment" v={d.razorpay_payment_id} full />}
        {d.fee_manual && <Field l="Fee status" v={`PAID — MANUAL BANK TRANSFER · UTR ${d.fee_manual.utr} · ₹${Number(d.fee_manual.amount).toLocaleString("en-IN")} · received ${new Date(d.fee_manual.received_on).toLocaleDateString("en-IN")}`} accent="#2FB999" full />}
        {d.authorized_at && <Field l="Authorized" v={`${new Date(d.authorized_at).toLocaleString()} by ${d.authorized_by || "—"}`} accent="#2FB999" full />}
        {d.first_login_at && <Field l="First login" v={new Date(d.first_login_at).toLocaleString()} accent="#C9A961" />}
        {d.revoked_reason && <Field l="Revocation reason" v={d.revoked_reason} accent="#C0392B" full />}
      </div>
      {!d.fee_paid && tab !== "revoked" && (
        <button data-testid={`director-manual-fee-${d.id}`} onClick={() => setFeeOpen(true)}
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#2FB999]/50 text-[#2FB999] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] hover:bg-[#2FB999] hover:text-black transition-colors">
          <Landmark size={12} /> Acknowledge offline fee (NEFT / RTGS)
        </button>
      )}
      <div className="mt-5 flex flex-wrap items-center gap-4" data-testid={`director-role-row-${d.id}`}>
        <RoleSelect api={api} directorId={d.id} value={journey.data?.role?.key} onChanged={(r) => { journey.setData(r); toast.success(r.role ? `Designation set · ${r.role.label}` : "Designation cleared", { description: r.role ? "A gold pulse will greet them on their next login." : undefined }); }} />
        {journey.data?.role_changed_at && <span className="font-mono text-[9px] text-[#8A8A82]">changed {new Date(journey.data.role_changed_at).toLocaleDateString("en-IN")}{journey.data.role_pulse_pending ? " · pulse pending" : ""}</span>}
        {journey.data?.inducted && (
          <button type="button" data-testid={`director-certificate-${d.id}`} onClick={async () => { try { const r = await api.get(`/admin/directors/${d.id}/journey/certificate.pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch { toast.error("Could not render the certificate"); } }}
                  className="ml-auto inline-flex items-center gap-1.5 text-[#C9A84C] font-grotesk text-[9px] uppercase tracking-[0.26em] hover:underline"><Download size={11} /> Stage 7 certificate</button>
        )}
      </div>
      <JourneyTimeline data={journey.data} admin onMark={mark} onClear={clear} busyStage={busyStage} />
      <FeeWaiverControls api={api} director={d} phases={journey.data?.vcb_fees} onChanged={(j) => journey.setData(j)} />
      <ManualFeeDialog api={api} director={d} open={feeOpen} onOpenChange={setFeeOpen} onSaved={(r) => { journey.setData(r); window.dispatchEvent(new Event("director-fee-acknowledged")); }} />
    </div>
  );
}

function Field({ l, v, accent, full }) {
  return (
    <div className={full ? "col-span-full" : ""}>
      <div className="font-grotesk text-[9px] uppercase tracking-[0.25em] text-[#8A8A82]">{l}</div>
      <div className="font-body text-sm mt-1" style={{ color: accent || "#F5F5F0" }}>{v}</div>
    </div>
  );
}

function AuthorizeModal({ director, onClose, onDone, api }) {
  const [note, setNote] = useState("");
  const [waive, setWaive] = useState(false);
  const [reason, setReason] = useState("");
  const [share, setShare] = useState(director.shareholding_pct || 0);
  const [busy, setBusy] = useState(false);
  const isRevoke = director.director_status === "active";
  const isEditShare = director.director_status === "active";

  const submit = async () => {
    setBusy(true);
    try {
      if (isRevoke) {
        if (!window.confirm(`Revoke ${director.name || director.email}'s Executive Board access?`)) { setBusy(false); return; }
        await api.post(`/admin/directors/${director.id}/revoke`, { reason });
        toast.success("Executive access revoked.");
      } else {
        if (!director.fee_paid && !waive) {
          toast.error("Fee not paid — tick 'Waive fee' to override.");
          setBusy(false); return;
        }
        await api.post(`/admin/directors/${director.id}/authorize`,
                        { waive_fee: waive, note, shareholding_pct: parseFloat(share) || 0 });
        toast.success(`Authorized · Board Portal unlocked for ${director.email}.`);
        onDone({ name: director.name || director.email, pct: parseFloat(share) || 0 });
        return;
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally { setBusy(false); }
  };

  const saveShare = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/directors/${director.id}/shareholding`,
                      { shareholding_pct: parseFloat(share) || 0 });
      toast.success(`Shareholding updated to ${(parseFloat(share)||0).toFixed(2)}%`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-sm border-2 border-[#C9A961]/40 bg-[#0f0d0a] p-8"
        data-testid="director-modal"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <span className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-[#C9A961]">
              · Super Master Command ·
            </span>
            <h3 className="font-serif text-2xl text-[#F5F5F0] mt-2">
              {isRevoke ? "Revoke Executive Access" : "Authorize Executive"}
            </h3>
            <p className="font-body text-sm text-[#8A8A82] mt-1">
              {director.name || director.email} · {director.business_name}
            </p>
          </div>
          <button onClick={onClose} className="text-[#8A8A82] hover:text-[#F5F5F0]"><X size={20} /></button>
        </div>

        {isRevoke ? (
          <>
            <div className="rounded-sm border border-[#C0392B]/40 bg-[#C0392B]/[0.06] p-4 mb-5 flex gap-3 items-start">
              <AlertTriangle size={18} className="text-[#C0392B] shrink-0 mt-0.5" />
              <p className="font-body text-sm text-[#F5F5F0]">
                Revoking access immediately locks the Board Portal and sends a
                formal Board note to <span className="text-[#C9A961]">{director.email}</span>.
              </p>
            </div>
            <label className="block mb-6">
              <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">Reason (sent in email)</span>
              <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                data-testid="director-revoke-reason"
                className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#C9A961] rounded-sm p-3 font-body text-sm text-[#F5F5F0] outline-none" />
            </label>

            {/* Shareholding editor for active director */}
            <div className="rounded-sm border border-[#C9A961]/30 bg-[#C9A961]/[0.04] p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">
                  · Shareholding on the Vinci cap-table ·
                </span>
                <span className="font-serif text-lg text-[#F5F5F0] tabular-nums">
                  {(parseFloat(share) || 0).toFixed(2)}%
                </span>
              </div>
              <input
                type="range" min="0" max="100" step="0.01"
                value={share}
                onChange={(e) => setShare(e.target.value)}
                data-testid="director-shareholding-slider"
                className="w-full accent-[#C9A961]"
              />
              <div className="flex gap-2 mt-3">
                <input type="number" min="0" max="100" step="0.01"
                       value={share}
                       onChange={(e) => setShare(e.target.value)}
                       data-testid="director-shareholding-input"
                       className="flex-1 bg-transparent border border-white/15 focus:border-[#C9A961] rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] outline-none" />
                <button
                  data-testid="director-shareholding-save"
                  onClick={saveShare}
                  disabled={busy}
                  className="rounded-sm border border-[#C9A961]/60 text-[#C9A961] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.28em] hover:bg-[#C9A961] hover:text-black transition-colors disabled:opacity-60"
                >
                  Save %
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-sm border border-[#C9A961]/20 bg-[#C9A961]/[0.05] p-4 mb-5 flex gap-3 items-start">
              <ShieldCheck size={18} className="text-[#C9A961] shrink-0 mt-0.5" />
              <div className="font-body text-sm text-[#F5F5F0] space-y-1">
                <div>Fee: <strong>₹{(director.fee_amount || 0).toLocaleString()}</strong> · {director.fee_paid ? "captured" : director.fee_waived ? "waived" : "outstanding"}</div>
                {director.razorpay_payment_id && (
                  <div className="text-[#8A8A82] text-xs">Payment: {director.razorpay_payment_id}</div>
                )}
              </div>
            </div>
            {!director.fee_paid && (
              <label className="flex items-center gap-3 mb-5 cursor-pointer">
                <input type="checkbox" checked={waive} onChange={(e) => setWaive(e.target.checked)}
                       data-testid="director-waive-fee"
                       className="w-4 h-4 accent-[#C9A961]" />
                <span className="font-body text-sm text-[#F5F5F0]">
                  <span className="font-grotesk uppercase tracking-[0.22em] text-[10px] text-[#C9A961]">Waive fee override</span>
                  {" — authorize without the ₹2.5L payment"}
                </span>
              </label>
            )}
            <label className="block mb-5">
              <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">Board note (optional, sent in welcome email)</span>
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                data-testid="director-authorize-note"
                className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#C9A961] rounded-sm p-3 font-body text-sm text-[#F5F5F0] outline-none" />
            </label>

            {/* Shareholding assign on authorization */}
            <div className="rounded-sm border border-[#C9A961]/30 bg-[#C9A961]/[0.04] p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">
                  · Assign shareholding on Vinci cap-table ·
                </span>
                <span className="font-serif text-lg text-[#F5F5F0] tabular-nums">
                  {(parseFloat(share) || 0).toFixed(2)}%
                </span>
              </div>
              <input
                type="range" min="0" max="100" step="0.01"
                value={share}
                onChange={(e) => setShare(e.target.value)}
                data-testid="director-shareholding-slider"
                className="w-full accent-[#C9A961]"
              />
              <input type="number" min="0" max="100" step="0.01"
                     value={share}
                     onChange={(e) => setShare(e.target.value)}
                     data-testid="director-shareholding-input"
                     placeholder="e.g. 5.00"
                     className="mt-2 w-full bg-transparent border border-white/15 focus:border-[#C9A961] rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] outline-none" />
              <p className="font-body text-[10px] text-[#8A8A82] mt-2 italic">
                Displayed on the Director's Founders Suite. Editable anytime after authorization.
              </p>
            </div>
          </>
        )}

        <div className="flex gap-3 mt-8">
          <button
            data-testid="director-modal-cancel"
            onClick={onClose}
            className="flex-1 rounded-sm border border-white/20 py-3 font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C9C9C2] hover:border-white/40"
          >Cancel</button>
          <button
            data-testid="director-modal-submit"
            onClick={submit}
            disabled={busy}
            className={`flex-1 rounded-sm py-3 font-grotesk text-[10px] uppercase tracking-[0.28em] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 ${
              isRevoke
                ? "bg-[#C0392B] text-white hover:bg-[#8b1e13]"
                : "bg-[#C9A961] text-black hover:bg-[#8B7333] hover:text-[#F5F5F0]"
            }`}
          >
            {busy ? "Working…" : isRevoke ? "Revoke access" : (
              <> <CheckCircle2 size={14} /> Authorize now</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
